// Recuperação automática das telas de erro (app/error.tsx,
// app/admin/error.tsx, app/global-error.tsx). Módulo puro (sem React, sem
// "server-only"), testado direto.
//
// Caso real (2026-10-16): cliente no celular viu "Não foi possível carregar
// esta página" sem código (erro no navegador). Causa mais provável: aba
// aberta antes de um deploy pedindo arquivos JS que não existem mais
// (ChunkLoadError / "Failed to fetch dynamically imported module") ou falha
// momentânea de rede ao buscar a página.
//
// Decisão:
//   - erro de carregamento de código/rede -> UM recarregamento completo da
//     página (busca o HTML e os arquivos novos). Guardado em sessionStorage
//     com horário: se a página recarregada falhar de novo em menos de
//     RELOAD_GUARD_MS, não recarrega outra vez (sem laço);
//   - qualquer outro erro (ou recarregamento já tentado) -> UMA nova
//     tentativa automática (retry/reset do Next) após um pequeno atraso,
//     também guardada por horário e por endereço;
//   - depois disso, a tela de erro atual continua sendo a resposta final.
//   - sessionStorage indisponível (modo privado/bloqueado): nunca recarrega
//     (não haveria como impedir um laço); tenta só UMA nova tentativa por
//     carregamento de página (guarda em memória).

export type ErrorRecoveryAction = "reload" | "retry" | "show";

export const ERROR_RECOVERY_STORAGE_KEY = "persi:error-recovery";
export const RELOAD_GUARD_MS = 60_000;
export const RETRY_GUARD_MS = 30_000;
export const AUTO_RETRY_DELAY_MS = 1_500;

const CHUNK_OR_NETWORK_PATTERNS: readonly RegExp[] = [
  /ChunkLoadError/i,
  /Loading (?:CSS )?chunk [\w./-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /Failed to fetch RSC payload/i,
  /Failed to fetch/i,
  /NetworkError when attempting to fetch resource/i,
  /^Load failed$/i,
  /network ?error/i,
  /Connection closed/i,
];

/** Erro de carregamento de código (deploy novo) ou de rede? */
export function isChunkOrNetworkError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const name = typeof (error as { name?: unknown }).name === "string" ? (error as { name: string }).name : "";
  const message = typeof (error as { message?: unknown }).message === "string" ? (error as { message: string }).message : "";
  if (name === "ChunkLoadError") return true;
  return CHUNK_OR_NETWORK_PATTERNS.some((pattern) => pattern.test(message));
}

interface RecoveryRecord {
  reloadAt?: number;
  retryAt?: number;
  retryPath?: string;
}

export interface RecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function readRecord(storage: RecoveryStorage): RecoveryRecord {
  const raw = storage.getItem(ERROR_RECOVERY_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const record = parsed as Record<string, unknown>;
    return {
      reloadAt: typeof record.reloadAt === "number" ? record.reloadAt : undefined,
      retryAt: typeof record.retryAt === "number" ? record.retryAt : undefined,
      retryPath: typeof record.retryPath === "string" ? record.retryPath : undefined,
    };
  } catch {
    return {};
  }
}

const memoryGuard = { retried: false };

/** Só para testes: zera a guarda em memória. */
export function resetErrorRecoveryMemoryGuard(): void {
  memoryGuard.retried = false;
}

/**
 * Decide a recuperação e já registra a tentativa escolhida. `storage` null
 * ou com falha = sem sessionStorage (só a guarda em memória).
 */
export function decideErrorRecovery(input: {
  error: unknown;
  path: string;
  now: number;
  storage: RecoveryStorage | null;
}): ErrorRecoveryAction {
  const chunkOrNetwork = isChunkOrNetworkError(input.error);
  let record: RecoveryRecord;
  try {
    if (!input.storage) throw new Error("NO_STORAGE");
    record = readRecord(input.storage);
  } catch {
    if (memoryGuard.retried) return "show";
    memoryGuard.retried = true;
    return "retry";
  }

  const recentReload = record.reloadAt !== undefined && input.now - record.reloadAt >= 0 && input.now - record.reloadAt < RELOAD_GUARD_MS;
  const recentRetry = record.retryAt !== undefined && record.retryPath === input.path && input.now - record.retryAt >= 0 && input.now - record.retryAt < RETRY_GUARD_MS;

  let action: ErrorRecoveryAction = "show";
  if (chunkOrNetwork && !recentReload) action = "reload";
  else if (!recentRetry) action = "retry";
  if (action === "show") return action;

  const next: RecoveryRecord = action === "reload"
    ? { ...record, reloadAt: input.now }
    : { ...record, retryAt: input.now, retryPath: input.path };
  try {
    input.storage.setItem(ERROR_RECOVERY_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Sem como gravar a guarda: recarregar poderia entrar em laço.
    if (action === "reload") {
      if (memoryGuard.retried) return "show";
      memoryGuard.retried = true;
      return "retry";
    }
  }
  return action;
}
